import { createContext, useContext, useState, ReactNode, useCallback } from 'react';

interface ConfidentialDocument {
  id: string;
  document_name: string;
  document_category: string;
  has_access?: boolean;
}

interface UnifiedContactPayload {
  listingId: string;
  listingTitle: string;
  sellerId: string;
  assetType?: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
  hiddenFields?: {
    price?: boolean;
    location?: boolean;
    serial?: boolean;
    hours?: boolean;
    contact?: boolean;
    email?: boolean;
    phone?: boolean;
    company?: boolean;
    modules?: boolean;
    shipping?: boolean;
  };
  confidentialDocuments?: ConfidentialDocument[];
  preselectedFields?: string[];
  preselectedDocuments?: string[];
  initialMode?: 'message' | 'info' | 'documents';
}

interface UnifiedContactContextType {
  isOpen: boolean;
  payload: UnifiedContactPayload | null;
  openUnifiedContact: (payload: UnifiedContactPayload) => void;
  closeUnifiedContact: () => void;
}

const UnifiedContactContext = createContext<UnifiedContactContextType | undefined>(undefined);

export function UnifiedContactProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [payload, setPayload] = useState<UnifiedContactPayload | null>(null);

  const openUnifiedContact = useCallback((newPayload: UnifiedContactPayload) => {
    // Use setTimeout to ensure any parent modal closes first
    setTimeout(() => {
      setPayload(newPayload);
      setIsOpen(true);
    }, 50);
    import('@/lib/analytics/track').then(({ trackEvent }) =>
      trackEvent({
        event_name: 'contact_dialog_opened',
        event_category: 'contact',
        asset_type: newPayload.assetType ?? null,
        asset_id: newPayload.listingId,
        properties: { mode: newPayload.initialMode ?? 'message' },
      })
    );
  }, []);

  const closeUnifiedContact = useCallback(() => {
    setIsOpen(false);
    setPayload(null);
  }, []);

  return (
    <UnifiedContactContext.Provider value={{ isOpen, payload, openUnifiedContact, closeUnifiedContact }}>
      {children}
    </UnifiedContactContext.Provider>
  );
}

export function useUnifiedContact() {
  const context = useContext(UnifiedContactContext);
  if (context === undefined) {
    throw new Error('useUnifiedContact must be used within a UnifiedContactProvider');
  }
  return context;
}
